import { useTranslation } from "react-i18next";
import useModalsStore from "../../../utils/stores/useModalsStore";
import useDialog from "../../../utils/hooks/useDialog";
import { fullName } from "../../../utils/helpers/userDisplay";
import { compareText } from "../../../utils/helpers/locale";
import Avatar from "../../Avatar/Avatar";

// Drill-down list opened by funnel / heatmap clicks. `teachers` is an array of
// real teacher objects (uuid, full_name, ...). Clicking a row opens the
// teacher overview modal.
export default function TeacherListModal({ title, teachers, focusStandardUuid }) {
  const { t } = useTranslation();
  const close = useModalsStore((s) => s.closeTeacherList);
  const openTeacherOverview = useModalsStore((s) => s.openTeacherOverview);
  const dialogRef = useDialog(close);

  const handleClick = (teacher) => {
    close();
    openTeacherOverview({ teacher, focusStandardUuid });
  };

  const sorted = [...teachers].sort((a, b) =>
    compareText(a.full_name || fullName(a), b.full_name || fullName(b)),
  );

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={close}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b flex justify-between items-center shrink-0">
          <h3 className="font-bold text-lg text-slate-800">
            {title} ({teachers.length})
          </h3>
          <button
            onClick={close}
            className="text-slate-400 hover:text-slate-800 text-2xl w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100"
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>

        {teachers.length > 0 ? (
          <ul className="p-5 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
            {sorted.map((teacher) => (
              <li key={teacher.uuid}>
                <button
                  onClick={() => handleClick(teacher)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2"
                >
                  <Avatar
                    user={teacher}
                    className="w-6 h-6 rounded-full bg-slate-700 text-white text-[9px] font-bold shrink-0"
                  />
                  <span className="truncate">
                    {teacher.full_name || fullName(teacher)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-5 text-center text-slate-500">
            {t("standards.noTeachersInGroup")}
          </div>
        )}
      </div>
    </div>
  );
}
